// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import * as echarts from 'echarts/core';
import {
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent,
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import {
  DEFAULT_GRAPH_CONFIG,
  EChartsOption,
  formatTooltip,
} from '../../config/graph-config';
import { CoolingChartConfig } from '../../interfaces/chart-config.interface';
import { LocalDateFormat } from 'src/app/_interfaces/data/local-data-format-model';
import { CoolingCompanionService } from '../../services/cooling-companion.service';
import localDateFormatsJson from '../../../../config/localDateFormats.json';
import { EChartsType } from 'echarts/core';
import { EChartsTooltipParam } from '../../interfaces/graph-config.interface';

echarts.use([
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LineChart,
  CanvasRenderer,
  UniversalTransition,
]);

@Component({
  selector: 'app-cooling-graph',
  standalone: true,
  imports: [],
  templateUrl: './cooling-graph.component.html',
  styleUrl: './cooling-graph.component.scss',
})
export class CoolingGraphComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @ViewChild('coolingGraph', { static: false }) chartContainer?: ElementRef;
  @Input() config?: CoolingChartConfig;
  @Input() dateFormat: LocalDateFormat = localDateFormatsJson[0];

  width = '100%';
  height = '500px';
  private myChart: EChartsType | null = null;

  constructor(private coolingCompanionService: CoolingCompanionService) {}

  ngAfterViewInit(): void {
    this.initChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateFormat'] && this.myChart && this.config) {
      this.updateChart();
    }
    // When config changes (new graph or new data), update & resize
    if (changes['config'] && this.myChart && this.config) {
      this.updateChart();
      setTimeout(() => this.myChart?.resize(), 0);
    }
  }

  ngOnDestroy(): void {
    if (this.myChart) {
      this.myChart.dispose();
    }
  }

  private initChart(): void {
    if (!this.chartContainer || !this.chartContainer.nativeElement) {
      return;
    }

    this.myChart = echarts.init(this.chartContainer.nativeElement);

    if (this.config) {
      this.updateChart();
    }
  }

  private updateChart(): void {
    if (!this.myChart || !this.config) {
      return;
    }
    const option = this.buildChartOption();
    this.myChart.setOption(option, true);
    setTimeout(() => this.myChart?.resize(), 0);
  }

  private getXAxisConfig(): EChartsOption['xAxis'] {
    return {
      type: 'time',
      position: 'bottom',
      axisLabel: {
        rotate: 45,
        align: 'right',
        verticalAlign: 'middle',
        inside: false,
        hideOverlap: true,
        margin: 12, // push label further from axis line
        formatter: (value: number | Date | string): string => {
          // ECharts passes value as ms (number) or ISO/string depending on data
          const iso =
            typeof value === 'number'
              ? new Date(value).toISOString()
              : String(value);

          return this.coolingCompanionService.formatEventDateTime(
            iso,
            this.dateFormat,
          );
        },
      },
    };
  }

  private buildChartOption(): any {
    return {
      ...DEFAULT_GRAPH_CONFIG,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
        // Use component-scoped formatter so we can access this.dateFormat
        formatter: (params: EChartsTooltipParam | EChartsTooltipParam[]) => {
          const list = Array.isArray(params) ? params : [params];
          const raw = list
            .map((item: EChartsTooltipParam) => {
              return formatTooltip(item);
            })
            .join('<br/>');

          const dateTime = list[0]?.value?.[0];
          const iso =
            typeof dateTime === 'number'
              ? new Date(dateTime).toISOString()
              : String(dateTime);

          const formattedDateTime =
            this.coolingCompanionService.formatEventDateTime(
              iso,
              this.dateFormat,
            );
          const displayTime = `Event: ${formattedDateTime}`;

          return displayTime + '<br/>' + raw;
        },
      },
      xAxis: this.getXAxisConfig(),
      yAxis: this.config?.yAxes,
      series: this.config?.series,
    };
  }
}
